import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API, useApp } from "@/AppContext";
import { Lightning, SignOut, ChatCircle, MagnifyingGlass, UserCircle, ChartLineUp, Briefcase, Gift, List, X, Tag, Gear, PlusCircle } from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { prefetchRoute } from "@/lib/routePrefetch";

// SVG flag components
const FlagUS = () => (
  <svg viewBox="0 0 7410 3900" className="w-4 h-3 rounded-sm overflow-hidden" aria-hidden="true">
    <rect width="7410" height="3900" fill="#b22234"/>
    <g fill="#fff">
      {[1,3,5,7,9,11].map((i)=>(<rect key={i} y={i*300} width="7410" height="300"/>))}
    </g>
    <rect width="2964" height="2100" fill="#3c3b6e"/>
  </svg>
);
const FlagMX = () => (
  <svg viewBox="0 0 30 20" className="w-4 h-3 rounded-sm overflow-hidden" aria-hidden="true">
    <rect x="0" y="0" width="10" height="20" fill="#006847"/>
    <rect x="10" y="0" width="10" height="20" fill="#ffffff"/>
    <rect x="20" y="0" width="10" height="20" fill="#ce1126"/>
    <ellipse cx="15" cy="10" rx="2.4" ry="1.6" fill="none" stroke="#7a3e12" strokeWidth="0.6"/>
  </svg>
);

export default function Navbar() {
  const { lang, toggleLang, t, user, logout, unread, authHeaders, hideChrome } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const isLanding = location.pathname === "/";
  const [isAdmin, setIsAdmin] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    axios.get(`${API}/admin/me`, { headers: authHeaders() })
      .then(() => setIsAdmin(true))
      .catch(() => setIsAdmin(false));
  }, [user, authHeaders]);

  // Close drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = async () => { await logout(); navigate("/"); };

  // Build the link list once so we can reuse it in mobile drawer
  const links = [];
  if (user?.role === "hirer") {
    links.push({ to: "/search", icon: MagnifyingGlass, label: t.nav.search, testid: "search" });
    links.push({ to: "/get-started?choose=1", icon: PlusCircle, label: t.nav.postJob, testid: "post-job" });
    links.push({ to: "/my-jobs", icon: Tag, label: t.nav.myJobs, testid: "my-jobs" });
  }
  if (user?.role === "laborer") {
    links.push({ to: "/jobs", icon: Briefcase, label: t.nav.jobs, testid: "jobs" });
    links.push({ to: "/dashboard", icon: UserCircle, label: t.nav.profile, testid: "profile" });
  }
  if (!user) {
    links.push({ to: "/for-workers", icon: null, label: lang === "es" ? "Trabajadores" : "For Workers", testid: "nav-for-workers" });
    links.push({ to: "/for-hirers", icon: null, label: lang === "es" ? "Contratantes" : "For Hirers", testid: "nav-for-hirers" });
  }
  if (user) {
    links.push({ to: "/inbox", icon: ChatCircle, label: t.nav.inbox, testid: "inbox" });
    if (user.is_affiliate_owner) {
      links.push({ to: "/affiliate", icon: Gift, label: "Partner", testid: "affiliate" });
    }
    // Refer & Earn shows in the header only for workers; hirers see it in the footer.
    if (user.role === "laborer") {
      links.push({ to: "/referrals", icon: Gift, label: t.referral.navLink, testid: "referrals" });
    }
    links.push({ to: "/account", icon: Gear, label: t.nav.account, testid: "account" });
  }

  if (hideChrome) return null;
  return (
    <header className="sticky top-0 z-50 bg-[#0A0A0A]/85 backdrop-blur-lg border-b border-white/10" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3.5 flex items-center gap-3">
        {/* LEFT — Brand */}
        <div className="flex items-center flex-shrink-0">
          <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
            <div className="w-8 h-8 rounded-md bg-[#FF7A1A] flex items-center justify-center group-hover:rotate-3 transition-transform">
              <Lightning size={18} weight="fill" color="#fff" />
            </div>
            <span className="font-display text-base md:text-lg tracking-tight text-white">CHAMBANOW</span>
          </Link>
        </div>

        {/* CENTER — Language toggle */}
        <div className="flex items-center justify-center gap-1.5 mx-auto flex-shrink-0" data-testid="lang-toggle">
          <button
            data-testid="lang-en-btn"
            onClick={() => lang !== "en" && toggleLang()}
            className={`flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-xs sm:text-sm font-semibold transition-all ${lang === "en" ? "border border-[#FF7A1A] text-white bg-[#FF7A1A]/10" : "border border-white/15 text-zinc-400 hover:text-white hover:border-white/30"}`}
            aria-label="English"
          >
            <FlagUS /> <span className="hidden sm:inline">English</span>
          </button>
          <button
            data-testid="lang-es-btn"
            onClick={() => lang !== "es" && toggleLang()}
            className={`flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-xs sm:text-sm font-semibold transition-all ${lang === "es" ? "border border-[#FF7A1A] text-white bg-[#FF7A1A]/10" : "border border-white/15 text-zinc-400 hover:text-white hover:border-white/30"}`}
            aria-label="Español"
          >
            <FlagMX /> <span className="hidden sm:inline">Español</span>
          </button>
        </div>

        {/* RIGHT — Desktop nav (lg+) + Mobile auth + hamburger */}
        <div className="flex items-center justify-end gap-1.5 flex-shrink-0">
          {/* Desktop links — only on lg+ */}
          <div className="hidden lg:flex items-center gap-1.5">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onMouseEnter={() => prefetchRoute(l.to)} onFocus={() => prefetchRoute(l.to)} className="btn-pill-ghost relative" title={l.label} data-testid={`nav-${l.testid}`}>
                {l.icon && <l.icon size={16} weight="bold" />} <span className="hidden xl:inline">{l.label}</span>
                {l.to === "/inbox" && unread > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-[#FF7A1A] text-white text-[10px] font-black rounded-full" data-testid="nav-inbox-badge">
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </Link>
            ))}
            {isAdmin && (
              <Link to="/admin" onMouseEnter={() => prefetchRoute("/admin")} onFocus={() => prefetchRoute("/admin")} className="btn-pill-ghost text-[#FF7A1A]" data-testid="nav-admin">
                <ChartLineUp size={16} weight="bold" /> <span className="hidden xl:inline">Admin</span>
              </Link>
            )}
          </div>

          {/* Auth buttons — always visible */}
          {user ? (
            <button onClick={handleLogout} className="hidden lg:inline-flex btn-pill-outline" data-testid="nav-logout">
              <SignOut size={14} weight="bold" /> <span className="hidden xl:inline">{t.nav.logout}</span>
            </button>
          ) : (
            <>
              {!isLanding && (
                <Link to="/login" onMouseEnter={() => prefetchRoute("/login")} onFocus={() => prefetchRoute("/login")} className="btn-pill-outline !px-3 !text-xs sm:!text-sm" data-testid="nav-login">{t.nav.login}</Link>
              )}
              <Link to="/signup" onMouseEnter={() => prefetchRoute("/signup")} onFocus={() => prefetchRoute("/signup")} className="btn-pill-primary !px-3 !text-xs sm:!text-sm" data-testid="nav-signup">{t.nav.signup}</Link>
            </>
          )}

          {/* Mobile hamburger — visible <lg when logged in */}
          {user && (
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  className="lg:hidden flex items-center justify-center w-10 h-10 rounded-md border border-white/15 text-white hover:bg-white/5 relative"
                  aria-label="Open menu"
                  data-testid="nav-hamburger"
                >
                  <List size={20} weight="bold" />
                  {unread > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-[#FF7A1A] text-white text-[10px] font-black rounded-full">
                      {unread > 99 ? "99+" : unread}
                    </span>
                  )}
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-zinc-950 border-l border-white/10 w-[280px] sm:w-[320px] p-0 text-white" data-testid="nav-mobile-drawer">
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                  <span className="font-display text-sm tracking-widest uppercase text-[#FF7A1A]">Menu</span>
                  <button onClick={() => setMobileOpen(false)} className="text-zinc-500 hover:text-white" aria-label="Close" data-testid="nav-mobile-close">
                    <X size={20} weight="bold" />
                  </button>
                </div>
                <nav className="px-3 py-4 space-y-1">
                  {links.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      className="flex items-center gap-3 px-3 py-3 rounded-md text-sm font-bold uppercase tracking-wider hover:bg-[#FF7A1A]/10 hover:text-[#FF7A1A] transition-colors"
                      data-testid={`nav-mobile-${l.testid}`}
                    >
                      {l.icon && <l.icon size={18} weight="bold" />}
                      <span className="flex-1">{l.label}</span>
                      {l.to === "/inbox" && unread > 0 && (
                        <span className="min-w-[20px] h-5 px-1.5 flex items-center justify-center bg-[#FF7A1A] text-white text-[10px] font-black rounded-full">
                          {unread > 99 ? "99+" : unread}
                        </span>
                      )}
                    </Link>
                  ))}
                  {isAdmin && (
                    <Link to="/admin" className="flex items-center gap-3 px-3 py-3 rounded-md text-sm font-bold uppercase tracking-wider text-[#FF7A1A] hover:bg-[#FF7A1A]/10" data-testid="nav-mobile-admin">
                      <ChartLineUp size={18} weight="bold" /> Admin
                    </Link>
                  )}
                  <div className="border-t border-white/10 mt-2 pt-2">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-3 py-3 rounded-md text-sm font-bold uppercase tracking-wider hover:bg-red-500/10 hover:text-red-400 transition-colors"
                      data-testid="nav-mobile-logout"
                    >
                      <SignOut size={18} weight="bold" /> {t.nav.logout}
                    </button>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          )}
        </div>
      </div>
    </header>
  );
}
